import * as z from "zod";
import { decimal, pagination } from "../index";

// The warmth of the accounts and contacts of Guestlist and its Insights (ADR-316 §1, ADR-315 §5): crm computes the
// warmth from the signals (the score is never shown, only its band and the tags), the best time to reach a person in
// the time zone of their country, and the views of Insights, each leading to a list to act on.

/** The warmth of a person or an account as Sales reads it (ADR-316 §1): words, never the score. */
export const warmthBands = ["hot", "warm", "cool", "cold"] as const;
export type WarmthBand = (typeof warmthBands)[number];

/**
 * The tags of an account (ADR-316 §1), the filter of the list of the accounts:
 * - `hot`, `engaged`: its warmth is Hot, or Warm;
 * - `cold_90d`: no human signal for 90 days, after some;
 * - `bounced`: an address of one of its people does not exist (ADR-320);
 * - `no_show_prone`: it missed at least a third of its appointments, after three;
 * - `link_opened_not_booked`: it opened a booking link in the last 30 days and booked nothing from it;
 * - `books_three_seasons`: it booked three seasons running, the last the current or the previous one;
 * - `lapsed`: it booked once, but neither the current season nor the previous one;
 * - `new_buyer`: its first booking is in the current season.
 */
export const accountTags = [
  "hot",
  "engaged",
  "cold_90d",
  "bounced",
  "no_show_prone",
  "link_opened_not_booked",
  "books_three_seasons",
  "lapsed",
  "new_buyer",
] as const;
export type AccountTag = (typeof accountTags)[number];

/** The tags of a contact: the same words, with `unsubscribed` (a "no" to every email) and without the seasons. */
export const contactTags = ["hot", "engaged", "cold_90d", "bounced", "unsubscribed", "link_opened_not_booked"] as const;
export type ContactTag = (typeof contactTags)[number];

/** Where the best time of a person comes from: their own signals, those of their account, or of their territory. */
export const bestTimeBases = ["contact", "account", "territory"] as const;
export type BestTimeBasis = (typeof bestTimeBases)[number];

/** The hour of the week a person answers most, in their time zone (ADR-316 §1): ISO day (1 is Monday) and hour. */
export const bestTimeDto = z
  .object({
    dow: z.number().int().min(1).max(7),
    hour: z.number().int().min(0).max(23),
    /** The IANA zone of the country of the account (`countryTimeZone`). */
    timeZone: z.string(),
    basis: z.enum(bestTimeBases),
  })
  .meta({ id: "CrmBestTime" });
export type BestTimeDto = z.output<typeof bestTimeDto>;

/** Why crm suggests an interest for a brand (ADR-316 §4): two human clicks on its links in 90 days, or its times seen. */
export const interestSuggestionReasons = ["clicks", "slots_viewed"] as const;
export type InterestSuggestionReason = (typeof interestSuggestionReasons)[number];

/**
 * Whether the person who reads can mark a suggested interest as a target brand (ADR-316 §4): `ready`; HubSpot keeps the
 * interests of the contact while it is the master (ADR-288), so the button says it and does not write; or the person
 * lacks the permission of the commercial data (ADR-303).
 */
export const interestMarks = ["ready", "kept_on_hubspot", "not_allowed"] as const;
export type InterestMark = (typeof interestMarks)[number];

/** The recent human signals the card lists: what the person did, never "is watching now" (ADR-316 §2). */
export const recentSignalKinds = ["open", "click", "link_opened", "slots_viewed", "booked"] as const;
export type RecentSignalKind = (typeof recentSignalKinds)[number];

/** How many of each part the warmth of an account shows at most. */
export const CRM_WARMTH_LIMITS = { recent: 10, suggestions: 10 } as const;

/** A season as Guestlist names it: crm has no name of a season, so its campaigns name it. */
export const crmSeasonDto = z
  .object({
    id: z.uuid(),
    /** The first campaigns of the season by start, joined: "February Market · Paris Days +2". */
    label: z.string(),
    startDate: z.iso.date().nullable(),
    endDate: z.iso.date().nullable(),
  })
  .meta({ id: "CrmSeason" });
export type CrmSeasonDto = z.output<typeof crmSeasonDto>;

/**
 * The warmth of an account on its card 360 (ADR-316 §1, plan §9.1): the band, the tags, the best time, the last season
 * it booked; and per person their band, tags and best time, the interests crm suggests and the recent human signals.
 * The parts about persons are missing for a delegated access (ADR-300 §4).
 */
export const crmAccountWarmthDto = z
  .object({
    band: z.enum(warmthBands),
    lastSignalAt: z.iso.datetime().nullable(),
    tags: z.array(z.enum(accountTags)),
    bestTime: bestTimeDto.nullable(),
    /** The last season with a booking: "Quiet since …" for a lapsed account. */
    lastBookedSeason: crmSeasonDto.nullable(),
    contacts: z
      .array(
        z.object({
          contactId: z.uuid(),
          band: z.enum(warmthBands),
          tags: z.array(z.enum(contactTags)),
          bestTime: bestTimeDto.nullable(),
        }),
      )
      .optional(),
    /** Undecided interests crm deduced, which "Mark as target brand" writes to the directory (ADR-316 §4). */
    suggestions: z
      .array(
        z.object({
          contactId: z.uuid(),
          brandOrgId: z.uuid(),
          brandName: z.string(),
          reason: z.enum(interestSuggestionReasons),
          suggestedAt: z.iso.datetime(),
          mark: z.enum(interestMarks),
        }),
      )
      .optional(),
    /** The human signals of the last 30 days, the newest first; the person only where the event kept it. */
    recent: z
      .array(
        z.object({
          at: z.iso.datetime(),
          kind: z.enum(recentSignalKinds),
          contactId: z.uuid().nullable(),
          campaign: z.object({ id: z.uuid(), name: z.string() }).nullable(),
        }),
      )
      .optional(),
  })
  .meta({ id: "CrmAccountWarmth" });
export type CrmAccountWarmthDto = z.output<typeof crmAccountWarmthDto>;

/**
 * The extended funnel of a campaign (plan §9.1): invited → delivered → opened → clicked → link opened → times seen →
 * booked → visited → ordered, every step in customers (an account once), the orders in euro. Opens are counted only
 * where the person allowed the measure (ADR-314 §1).
 */
export const campaignEngagementDto = z
  .object({
    invited: z.number().int(),
    delivered: z.number().int(),
    opened: z.number().int(),
    clicked: z.number().int(),
    linkOpened: z.number().int(),
    slotsViewed: z.number().int(),
    booked: z.number().int(),
    visited: z.number().int(),
    ordered: z.number().int(),
    /** The orders of the season of the campaign for the brands it invited the customer to, in euro (ADR-290 §3). */
    orderedEur: decimal,
    /** Orders without a rate to the euro: left out of `orderedEur`, said apart. */
    ordersWithoutRate: z.number().int(),
  })
  .meta({ id: "CrmCampaignEngagement" });
export type CampaignEngagementDto = z.output<typeof campaignEngagementDto>;

/**
 * How likely a booked customer is not to come (ADR-316 §3), computed at the read of Today from its appointments held:
 * `high` after two at least, half of them missed; `some` with one missed.
 */
export const noShowRisks = ["high", "some"] as const;
export type NoShowRisk = (typeof noShowRisks)[number];

/** The views of Insights (ADR-315 §5); the Sellers view is not built (B-183, on its assumption). */
export const insightViews = ["season", "first_season", "brands_territories", "arrivals"] as const;
export type InsightView = (typeof insightViews)[number];

const territory = z.string().trim().min(1).max(100);

export const getCrmInsightsInput = z.object({
  view: z.enum(insightViews),
  /** The season looked at; the latest started without. */
  seasonId: z.uuid().optional(),
  /** The sales territory of the customers counted (ADR-289); every one without. */
  territory: territory.optional(),
});
export type GetCrmInsightsInput = z.input<typeof getCrmInsightsInput>;

/** How many seasons Insights offers and compares at most. */
export const CRM_INSIGHTS_LIMITS = {
  seasons: 12,
  firstSeasons: 8,
  brands: 12,
  territories: 40,
  countries: 20,
} as const;

/** What a season counted by its day `day` (from its first campaign's start). */
export const seasonFiguresDto = z.object({
  invited: z.number().int(),
  /** Customers with an appointment held or due by that day. */
  appointments: z.number().int(),
  visited: z.number().int(),
  ordered: z.number().int(),
  orderedEur: decimal,
});
export type SeasonFiguresDto = z.output<typeof seasonFiguresDto>;

export const crmInsightsDto = z
  .object({
    view: z.enum(insightViews),
    /** The seasons with campaigns, the latest first: the values of the picker. */
    seasons: z.array(crmSeasonDto),
    seasonId: z.uuid().nullable(),
    /** The season against the one before at the same day; null for another view. */
    season: z
      .object({
        day: z.number().int(),
        previous: crmSeasonDto.nullable(),
        current: seasonFiguresDto,
        previousAtDay: seasonFiguresDto.nullable(),
        /** Booked appointments still ahead in the season, which no comparison has yet. */
        bookedAhead: z.number().int(),
        /** Customers with an appointment by each day, cumulative, this season and the one before. */
        curve: z.array(
          z.object({
            day: z.number().int(),
            current: z.number().int().nullable(),
            previous: z.number().int().nullable(),
          }),
        ),
      })
      .nullable(),
    /** The buyers by the season of their first booking, and how many booked again each season after. */
    firstSeason: z
      .object({
        seasons: z.array(crmSeasonDto),
        rows: z.array(
          z.object({ seasonId: z.uuid(), buyers: z.number().int(), booked: z.array(z.number().int().nullable()) }),
        ),
      })
      .nullable(),
    /** The interest of each territory for each brand in the season, without persons, counted last night. */
    brandsTerritories: z
      .object({
        brands: z.array(z.object({ id: z.uuid(), name: z.string() })),
        territories: z.array(z.string()),
        cells: z.array(
          z.object({
            brandOrgId: z.uuid(),
            territory: z.string(),
            contacts: z.number().int(),
            humanClicks: z.number().int(),
            slotViews: z.number().int(),
          }),
        ),
        computedAt: z.iso.datetime().nullable(),
      })
      .nullable(),
    /** Where the buyers of the season come from, by country: invited, booked, arrived. */
    arrivals: z
      .object({
        rows: z.array(
          z.object({
            countryCode: z.string().nullable(),
            territory: z.string().nullable(),
            invited: z.number().int(),
            booked: z.number().int(),
            visited: z.number().int(),
          }),
        ),
        /** The countries past the limit, counted together. */
        other: z.object({ invited: z.number().int(), booked: z.number().int(), visited: z.number().int() }).nullable(),
      })
      .nullable(),
  })
  .meta({ id: "CrmInsights" });
export type CrmInsightsDto = z.output<typeof crmInsightsDto>;

/** A cell of Brands × territories opened: the customers of the territory who showed interest and did not book it. */
export const listCrmInsightBuyersInput = z.object({
  seasonId: z.uuid(),
  brandOrgId: z.uuid(),
  territory,
  ...pagination({ defaultSize: 25, maxSize: 50 }),
});
export type ListCrmInsightBuyersInput = z.input<typeof listCrmInsightBuyersInput>;

export const crmInsightBuyerDto = z
  .object({
    id: z.uuid(),
    name: z.string(),
    countryCode: z.string().nullable(),
    customerCode: z.string().nullable(),
    band: z.enum(warmthBands),
    /** Its people who clicked on the brand or saw its times in the last 90 days. */
    interested: z.number().int(),
    /** Whether a campaign of the season already invited it to the brand. */
    invited: z.boolean(),
  })
  .meta({ id: "CrmInsightBuyer" });
export type CrmInsightBuyerDto = z.output<typeof crmInsightBuyerDto>;

/**
 * The IANA time zone of a country (ISO 3166-1 alpha-2), for the best time of a person (ADR-316 §1): the zone of the
 * capital, or of the main business city of a country of many zones. Tomorrow's clock for a country not here.
 */
export const COUNTRY_TIME_ZONES: Readonly<Record<string, string>> = {
  AE: "Asia/Dubai",
  AR: "America/Argentina/Buenos_Aires",
  AT: "Europe/Vienna",
  AU: "Australia/Sydney",
  BE: "Europe/Brussels",
  BG: "Europe/Sofia",
  BH: "Asia/Bahrain",
  BR: "America/Sao_Paulo",
  CA: "America/Toronto",
  CH: "Europe/Zurich",
  CL: "America/Santiago",
  CN: "Asia/Shanghai",
  CO: "America/Bogota",
  CY: "Asia/Nicosia",
  CZ: "Europe/Prague",
  DE: "Europe/Berlin",
  DK: "Europe/Copenhagen",
  EE: "Europe/Tallinn",
  EG: "Africa/Cairo",
  ES: "Europe/Madrid",
  FI: "Europe/Helsinki",
  FR: "Europe/Paris",
  GB: "Europe/London",
  GR: "Europe/Athens",
  HK: "Asia/Hong_Kong",
  HR: "Europe/Zagreb",
  HU: "Europe/Budapest",
  ID: "Asia/Jakarta",
  IE: "Europe/Dublin",
  IL: "Asia/Jerusalem",
  IN: "Asia/Kolkata",
  IS: "Atlantic/Reykjavik",
  IT: "Europe/Rome",
  JP: "Asia/Tokyo",
  KR: "Asia/Seoul",
  KW: "Asia/Kuwait",
  KZ: "Asia/Almaty",
  LB: "Asia/Beirut",
  LT: "Europe/Vilnius",
  LU: "Europe/Luxembourg",
  LV: "Europe/Riga",
  MA: "Africa/Casablanca",
  MC: "Europe/Monaco",
  MT: "Europe/Malta",
  MX: "America/Mexico_City",
  MY: "Asia/Kuala_Lumpur",
  NG: "Africa/Lagos",
  NL: "Europe/Amsterdam",
  NO: "Europe/Oslo",
  NZ: "Pacific/Auckland",
  PE: "America/Lima",
  PH: "Asia/Manila",
  PL: "Europe/Warsaw",
  PT: "Europe/Lisbon",
  QA: "Asia/Qatar",
  RO: "Europe/Bucharest",
  RS: "Europe/Belgrade",
  RU: "Europe/Moscow",
  SA: "Asia/Riyadh",
  SE: "Europe/Stockholm",
  SG: "Asia/Singapore",
  SI: "Europe/Ljubljana",
  SK: "Europe/Bratislava",
  SM: "Europe/San_Marino",
  TH: "Asia/Bangkok",
  TR: "Europe/Istanbul",
  TW: "Asia/Taipei",
  UA: "Europe/Kyiv",
  US: "America/New_York",
  VN: "Asia/Ho_Chi_Minh",
  ZA: "Africa/Johannesburg",
};

/** Tomorrow's clock (`CAMPAIGN_CLOCK` of crm): the zone of a country the map does not have. */
export const FALLBACK_TIME_ZONE = "Europe/Rome";

/** The time zone of the country of an account, Tomorrow's when it has none or the map does not know it. */
export const countryTimeZone = (countryCode: string | null | undefined): string =>
  (countryCode && COUNTRY_TIME_ZONES[countryCode.toUpperCase()]) || FALLBACK_TIME_ZONE;
